import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, CheckCircle, Scale, Users, FileSignature, ArrowRight, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { serviceAPI, openWhatsApp } from '../lib/api';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { getField } from '../lib/localize';

const ServiceConnect = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { settings } = useSite();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  const categories = [
    { id: 'all', label: t('categories.all'), icon: Users },
    { id: 'lawyer', label: t('categories.lawyer'), icon: Scale },
    { id: 'agent', label: t('categories.agent'), icon: Users },
    { id: 'document_assistant', label: t('categories.documentAssistant'), icon: FileSignature },
  ];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await serviceAPI.getAll();
        setServices(res.data);
        setFilteredServices(res.data);
      } catch (err) {
        console.error('Failed to fetch services:', err);
      }
      setLoading(false);
    };
    fetchServices();
  }, []);

  useEffect(() => {
    let result = services;
    
    if (activeCategory !== 'all') {
      result = result.filter(s => s.category === activeCategory);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(s => 
        (s.name || '').toLowerCase().includes(searchLower) ||
        (s.name_en || '').toLowerCase().includes(searchLower) ||
        (s.location || '').toLowerCase().includes(searchLower) ||
        (s.location_en || '').toLowerCase().includes(searchLower) ||
        (s.bio || '').toLowerCase().includes(searchLower) ||
        (s.bio_en || '').toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredServices(result);
  }, [search, activeCategory, services]);

  const categoryLabels = {
    lawyer: t('categories.lawyer'),
    agent: t('categories.agent'),
    document_assistant: t('categories.documentAssistant'),
  };

  const categoryIcons = {
    lawyer: Scale,
    agent: Users,
    document_assistant: FileSignature,
  };

  const handleWhatsApp = (service, e) => {
    e.preventDefault();
    e.stopPropagation();
    const message = settings.whatsapp_default_message || `আসসালামু আলাইকুম, ${service.name}। ভূমি প্ল্যাটফর্ম থেকে আপনার সাথে যোগাযোগ করছি।`;
    openWhatsApp(service.whatsapp || service.phone, message);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={language === 'en' ? 'Verified Service Providers' : 'যাচাইকৃত সেবাদাতা'}
        description={language === 'en'
          ? 'Find verified land lawyers, agents and document assistants in Bangladesh. Connect with trusted professionals for Namjari, Khatian, deeds and more.'
          : 'বাংলাদেশে যাচাইকৃত ভূমি উকিল, এজেন্ট এবং দলিল সহকারী খুঁজুন। নামজারি, খতিয়ান, দলিলের জন্য বিশ্বস্ত পেশাদারদের সাথে যোগাযোগ করুন।'
        }
        path="/services"
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className={`pt-20 md:pt-24 pb-8 md:pb-12 border-b ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="services-title">
              {t('services.title')}
            </h1>
            <p className={`text-sm sm:text-base md:text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('services.subtitle')}
            </p>
          </div>

          {/* Search */}
          <div className="mt-6 md:mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder={t('services.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-12 h-11 md:h-12 rounded-full ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : 'border-gray-300'} focus:border-[#2E7D32] focus:ring-[#2E7D32]`}
                data-testid="search-services"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mt-4 md:mt-6 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setActiveCategory(cat.id)}
                size="sm"
                className={`rounded-full text-xs sm:text-sm ${
                  activeCategory === cat.id
                    ? 'bg-[#2E7D32] hover:bg-[#1B5E20] text-white'
                    : isDark
                      ? 'border-white/20 text-gray-300 hover:border-[#2E7D32] hover:text-[#2E7D32]'
                      : 'border-gray-300 text-gray-600 hover:border-[#2E7D32] hover:text-[#2E7D32]'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon size={14} className="mr-1 sm:mr-2" />
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`rounded-xl p-4 md:p-6 animate-pulse ${isDark ? 'bg-[#111]' : 'bg-white'}`}>
                  <div className="flex items-center gap-3 md:gap-4 mb-4">
                    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <div className="flex-1">
                      <div className={`h-4 md:h-5 rounded w-3/4 mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`h-3 md:h-4 rounded w-1/2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    </div>
                  </div>
                  <div className={`h-3 md:h-4 rounded w-full mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-3 md:h-4 rounded w-2/3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-12 md:py-16">
              <Users className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className={`text-lg md:text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('services.noResults')}</h3>
              <p className={`text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('hub.tryAnother')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredServices.map((service) => {
                const IconComponent = categoryIcons[service.category] || Users;
                return (
                  <div
                    key={service.id}
                    className={`rounded-xl p-4 md:p-6 border transition-all duration-200 ${
                      isDark 
                        ? 'bg-[#111] border-white/10 hover:border-[#2E7D32]' 
                        : 'bg-white border-gray-200 hover:border-[#2E7D32] hover:shadow-lg'
                    }`}
                    data-testid={`service-${service.slug}`}
                  >
                    <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                      <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#2E7D32]/20' : 'bg-[#E8F5E9]'}`}>
                        <IconComponent className="text-[#2E7D32]" size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`text-base md:text-lg font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{getField(service, 'name', language)}</h3>
                          {service.verified && (
                            <div className="verified-pulse flex-shrink-0">
                              <CheckCircle className="text-[#2E7D32]" size={16} />
                            </div>
                          )}
                        </div>
                        <Badge variant="secondary" className="mt-1 bg-[#E8F5E9] text-[#2E7D32] hover:bg-[#E8F5E9] text-xs">
                          {categoryLabels[service.category]}
                        </Badge>
                      </div>
                    </div>

                    <p className={`text-xs sm:text-sm mb-3 md:mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{getField(service, 'bio', language)}</p>

                    <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4">
                      <div className={`flex items-center gap-2 text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <MapPin size={14} className="text-[#2E7D32] flex-shrink-0" />
                        <span className="truncate">{getField(service, 'location', language)}</span>
                      </div>
                      {service.fee_range && (
                        <div className={`flex items-center gap-2 text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="text-[#2E7D32] font-medium">{t('services.feeRange')}:</span>
                          <span className="truncate">{getField(service, 'fee_range', language)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Link to={`/services/${service.slug}`} className="flex-1">
                        <Button className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-lg text-xs sm:text-sm h-9 md:h-10">
                          {t('services.details')}
                          <ArrowRight size={14} className="ml-1 md:ml-2" />
                        </Button>
                      </Link>
                      {(service.whatsapp || service.phone) && (
                        <Button
                          variant="outline"
                          onClick={(e) => handleWhatsApp(service, e)}
                          className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-lg h-9 md:h-10 px-3"
                          data-testid={`whatsapp-${service.slug}`}
                        >
                          <MessageCircle size={18} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className={`py-12 md:py-16 ${isDark ? 'bg-[#0D330F]' : 'bg-[#E8F5E9]'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('services.areYouProvider')}
          </h2>
          <p className={`text-sm md:text-base mb-4 md:mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('services.joinPlatform')}
          </p>
          <Link to="/contact">
            <Button className="bg-[#FBC02D] hover:bg-[#F9A825] text-gray-900 rounded-full px-6 md:px-8 py-2.5 md:py-3 font-semibold text-sm md:text-base">
              {t('services.apply')}
            </Button>
          </Link>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default ServiceConnect;
